import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  itemCaption?: string;
  itemLabel: string;
  /** Optional ticket id / reference shown above the subject (e.g. TXT-10446). */
  itemRef?: string;
  warningMessage?: string | null;
  confirmButtonLabel?: string;
  onConfirm: () => void | Promise<void>;
  confirming?: boolean;
}

export function DeleteConfirmDialog({
  open,
  onOpenChange,
  title = "Delete this item?",
  description = "This action is permanent and cannot be undone.",
  itemCaption = "Item",
  itemLabel,
  itemRef,
  warningMessage,
  confirmButtonLabel = "Delete",
  onConfirm,
  confirming = false,
}: DeleteConfirmDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={next => {
        if (!confirming) onOpenChange(next);
      }}
    >
      <AlertDialogContent
        className={cn(
          "flex w-[min(440px,calc(100vw-2rem))] max-h-[min(90vh,720px)] flex-col gap-0 overflow-hidden border-border/60 p-0",
          "shadow-2xl sm:rounded-xl"
        )}
      >
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <AlertDialogHeader className="space-y-0 px-6 pb-5 pt-6 text-left">
            <div className="flex min-w-0 items-start gap-4">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-destructive/10 ring-1 ring-destructive/20"
                aria-hidden
              >
                <Trash2 className="h-5 w-5 text-destructive" />
              </div>
              <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
                <AlertDialogTitle className="text-[17px] font-semibold leading-snug tracking-tight text-foreground">
                  {title}
                </AlertDialogTitle>
                <AlertDialogDescription className="break-words text-[13px] leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
                  {description}
                </AlertDialogDescription>
              </div>
            </div>

            <div className="mt-5 flex min-w-0 flex-col gap-3">
              <div className="min-w-0 overflow-hidden rounded-lg border border-border/80 bg-muted/30 px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {itemCaption}
                </p>
                {itemRef ? (
                  <p className="mt-1 truncate font-mono text-xs font-semibold text-muted-foreground">
                    {itemRef}
                  </p>
                ) : null}
                <p
                  className={cn(
                    "line-clamp-3 break-all text-sm font-medium leading-snug text-foreground [overflow-wrap:anywhere]",
                    itemRef ? "mt-1" : "mt-1"
                  )}
                  title={itemLabel}
                >
                  {itemLabel}
                </p>
              </div>

              {warningMessage ? (
                <div className="flex min-w-0 items-start gap-2 rounded-md border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 dark:border-amber-900/40 dark:bg-amber-950/30">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="min-w-0 break-words text-xs leading-relaxed text-amber-900/90 [overflow-wrap:anywhere] dark:text-amber-100/90">
                    {warningMessage}
                  </p>
                </div>
              ) : null}
            </div>
          </AlertDialogHeader>
        </div>

        <AlertDialogFooter className="flex shrink-0 flex-row justify-end gap-2 border-t border-border/80 bg-muted/20 px-6 py-4 sm:space-x-0">
          <AlertDialogCancel
            disabled={confirming}
            className="mt-0 h-10 min-w-[96px] rounded-lg border-border/80 bg-background px-4 font-medium shadow-sm"
          >
            Cancel
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={confirming}
            className="h-10 min-w-[120px] gap-2 rounded-lg px-4 font-semibold shadow-sm"
            onClick={() => void onConfirm()}
          >
            {confirming ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting…
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                {confirmButtonLabel}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
